import { useCallback, useState } from "react";
import {
  GetJournalsUseCase,
  JournalEntity,
  JournalListStatus,
  PaginationInfo,
} from "../../domain";
import { JournalRepositoryImpl } from "../../data/repositories/JournalRepositoryImpl";

const journalRepository = new JournalRepositoryImpl();
const getJournalsUseCase = new GetJournalsUseCase(journalRepository);

export interface JournalListParams {
  page?: number;
  limit?: number;
  status?: JournalListStatus;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: "created_at" | "updated_at" | "date" | "approved_at" | "journal_number";
  sortOrder?: "asc" | "desc";
}

export const useJournalList = () => {
  const [journals, setJournals] = useState<JournalEntity[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchJournals = useCallback(async (params: JournalListParams = {}) => {
    setLoading(true);
    setError(null);

    const limit = params.limit ?? 10;
    const page = params.page ?? 1;

    try {
      const result = await getJournalsUseCase.execute({
        limit,
        offset: Math.max(page - 1, 0) * limit,
        status: params.status ?? "all",
        search: params.search?.trim() || undefined,
        date_from: params.dateFrom,
        date_to: params.dateTo,
        sort_by: params.sortBy ?? "created_at",
        sort_order: params.sortOrder ?? "desc",
      });

      setJournals(result.journals);
      setPagination(result.pagination);
    } catch (error: any) {
      setError(error.message || "Failed to load journals");
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    journals,
    pagination,
    loading,
    error,
    fetchJournals,
    clearError,
  };
};
